import { useEffect, useState, useRef } from "react";
import { Globe, ArrowRight, ArrowLeft, Hand, RefreshCw, Radio, ListVideo, Play, Trash2, X, Plus, Search, UserPlus, Clapperboard, LogOut, AlertTriangle } from "lucide-react";
import StreamingServices from "@/components/StreamingServices";
import SubtitleOverlay from "@/components/SubtitleOverlay";
import SearchPanel from "@/components/SearchPanel";
import { VideoPlayer, extractVideoId, isBlockedSite, getHost } from "@/components/VideoPlayer";

function BufferingSpinner({ playerRef }) {
  const [buffering, setBuffering] = useState(false);
  useEffect(() => {
    const id = setInterval(() => {
      const st = playerRef.current?.getState?.();
      setBuffering(st === 3); // 3 = YT.PlayerState.BUFFERING
    }, 300);
    return () => clearInterval(id);
  }, [playerRef]);
  if (!buffering) return null;
  return (
    <div data-testid="buffering-spinner" className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none">
      <div className="w-12 h-12 rounded-full border-2 border-white/20 border-t-[#00D9FF] animate-spin" />
    </div>
  );
}

function NowPlayingBanner({ url }) {
  const [title, setTitle] = useState(null);
  const [visible, setVisible] = useState(true);
  const yt = extractVideoId(url);

  useEffect(() => {
    let active = true;
    setTitle(null);
    setVisible(true);
    const hide = setTimeout(() => { if (active) setVisible(false); }, 6000);
    if (yt) {
      fetch(`https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${yt}&format=json`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => { if (active && d && d.title) setTitle(d.title); })
        .catch(() => {});
    }
    return () => { active = false; clearTimeout(hide); };
  }, [url, yt]);

  const label = title || getHost(url) || url;
  return (
    <div
      data-testid="now-playing-banner"
      className={`absolute top-0 max-md:top-11 left-0 right-0 z-20 pointer-events-none transition-opacity duration-500 ${visible ? "opacity-100" : "opacity-0"}`}
    >
      <div className="flex items-center gap-2 px-4 py-2 md:pl-44 bg-gradient-to-b from-black/85 to-transparent">
        <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#00D9FF] shrink-0">
          <Radio className="w-3.5 h-3.5 animate-pulse" /> Now Playing
        </span>
        <span className="text-sm font-semibold text-white truncate">{label}</span>
      </div>
    </div>
  );
}

function ExternalSyncNotice({ browseUrl }) {
  const KEY = "ibeflix_extnotice_dismissed";
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(KEY) === "1"; } catch (e) { return false; }
  });
  const [expanded, setExpanded] = useState(true);
  const [pinned, setPinned] = useState(false);
  const timerRef = useRef(null);

  // re-surface (expanded) whenever the driver switches to a new external URL
  useEffect(() => { setExpanded(true); setPinned(false); }, [browseUrl]);

  // auto-collapse to a small icon after 6s unless the user pinned it open
  useEffect(() => {
    clearTimeout(timerRef.current);
    if (expanded && !pinned) {
      timerRef.current = setTimeout(() => setExpanded(false), 6000);
    }
    return () => clearTimeout(timerRef.current);
  }, [expanded, pinned]);

  if (dismissed) return null;

  const dismiss = () => {
    try { localStorage.setItem(KEY, "1"); } catch (e) { /* storage blocked */ }
    setDismissed(true);
  };

  if (!expanded) {
    return (
      <button
        data-testid="external-sync-info-icon"
        onClick={() => { setExpanded(true); setPinned(true); }}
        title="Sync info for external sites"
        className="absolute top-3 max-md:top-14 right-3 z-30 w-9 h-9 rounded-full bg-black/70 backdrop-blur-md border border-amber-500/40 text-amber-300 flex items-center justify-center hover:bg-black/90 active:scale-90 transition-all animate-in fade-in duration-200"
      >
        <AlertTriangle className="w-4 h-4" />
      </button>
    );
  }

  return (
    <div
      data-testid="external-sync-notice"
      onClick={() => setPinned(true)}
      className="absolute top-3 max-md:top-14 right-3 z-30 max-w-[320px] cursor-default animate-in fade-in slide-in-from-top-2 duration-300"
    >
      <div className="flex items-start gap-2 px-3.5 py-2.5 rounded-2xl bg-black/85 backdrop-blur-md border border-amber-500/30 text-amber-200 text-xs shadow-xl">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        <span className="leading-relaxed">
          Everyone loads this site, but play/pause/seek can't auto-sync on external sites — use the IBEFLIX browser extension, or hit Resync to realign.
        </span>
        <button
          data-testid="external-sync-dismiss"
          onClick={(e) => { e.stopPropagation(); dismiss(); }}
          title="Dismiss — don't show again"
          className="shrink-0 -mr-1 -mt-0.5 w-6 h-6 rounded-full hover:bg-white/10 flex items-center justify-center text-amber-200/80 hover:text-white transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

export default function BrowseTogether({ browseUrl, isDriver, drivers = [], viewers = [], myId, onInviteCoDriver, onRemoveDriver, onStepDown, onNavigate, onBack, onRequestControl, onResync, playerRef, onAction, onVideoStatus, viewerLanguage, onUploadCaptions, onSeek, captionSignal, syncStatus, upNext = [], onAddNext, onRemoveNext, onPlayNext, region, onPickingChange, pickerActive, openSearchRef }) {
  const [input, setInput] = useState(browseUrl || "");
  const [nextUrl, setNextUrl] = useState("");
  const [queueOpen, setQueueOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const canControl = isDriver || drivers.length === 0;
  const lead = drivers[0];
  const driverIds = new Set(drivers.map((d) => d.id));
  const candidates = viewers.filter((v) => !driverIds.has(v.id));
  const seatsFull = drivers.length >= 2;

  const openSearch = () => { setSearchOpen(true); onPickingChange && onPickingChange(true); };
  const closeSearch = () => { setSearchOpen(false); onPickingChange && onPickingChange(false); };
  const pickVideo = (videoId) => { onNavigate(`https://www.youtube.com/watch?v=${videoId}`); closeSearch(); };
  if (openSearchRef) openSearchRef.current = openSearch;

  useEffect(() => { setInput(browseUrl || ""); }, [browseUrl]);

  const go = () => {
    let url = input.trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) url = "https://" + url;
    onNavigate(url);
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* discovery bar */}
      <div className={`px-4 py-3 border-b border-white/5 bg-[#0A0E27] shrink-0 space-y-2 ${browseUrl ? "max-md:hidden" : ""}`}>
        {canControl && (
          <button
            data-testid="open-search-button"
            onClick={openSearch}
            className="w-full flex items-center gap-2 bg-[#0F1636] border border-white/10 rounded-full px-4 h-11 text-left text-[#71717A] hover:border-[#00D9FF] hover:text-[#A0A0AB] transition-colors"
          >
            <Search className="w-4 h-4 text-[#00D9FF] shrink-0" />
            <span className="text-sm">Search for videos to watch...</span>
          </button>
        )}
        <div className="flex items-center gap-2">
          <div className={`flex-1 flex items-center gap-2 bg-[#0F1636] border rounded-full px-4 h-10 transition-colors ${canControl ? "border-white/10 focus-within:border-[#00D9FF]" : "border-white/5"}`}>
            <Globe className="w-4 h-4 text-[#71717A] shrink-0" />
            <input
              data-testid="browse-url-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && canControl) go(); }}
              disabled={!canControl}
              placeholder={canControl ? "or paste any link (YouTube, Tubi, Pluto…)" : "Only the driver can navigate"}
              className="flex-1 bg-transparent outline-none text-sm text-[#F1F1F5] disabled:cursor-not-allowed placeholder:text-[#71717A]"
            />
          </div>
          {canControl ? (
            <button
              data-testid="browse-go-button"
              onClick={go}
              className="h-10 px-4 bg-white/5 border border-white/10 text-white font-semibold rounded-full hover:bg-[#00D9FF]/20 hover:border-[#00D9FF]/40 transition-colors active:scale-95 shrink-0 flex items-center gap-1.5 text-sm"
            >
              Go <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              data-testid="request-control-button"
              onClick={onRequestControl}
              className="h-10 px-4 bg-white/5 border border-white/10 text-white font-semibold rounded-full hover:bg-white/10 transition-colors active:scale-95 shrink-0 flex items-center gap-1.5 text-sm"
            >
              <Hand className="w-4 h-4" /> Request
            </button>
          )}
        </div>
      </div>

      {/* driver bar */}
      <div className={`flex items-center gap-2 px-4 py-2 border-b border-white/5 bg-[#0A0E27] shrink-0 text-xs ${browseUrl ? "max-md:hidden" : ""}`} data-testid="driver-bar">
        <Clapperboard className="w-3.5 h-3.5 text-[#00D9FF] shrink-0" />
        {drivers.length > 0 ? (
          <span className="text-[#A0A0AB] flex items-center gap-1.5 flex-wrap">
            {drivers.map((d, i) => (
              <span key={d.id} className="text-[#F1F1F5] font-semibold" data-testid={`driver-name-${d.name}`}>
                {d.flag} {d.name}{i < drivers.length - 1 ? " &" : ""}
              </span>
            ))}
            <span>{isDriver ? (drivers.length > 1 ? "— you're co-driving" : "— you're driving") : drivers.length > 1 ? "are driving" : "is driving"}</span>
          </span>
        ) : (
          <span className="text-[#A0A0AB]">No driver yet — navigate to take the wheel</span>
        )}
        {!isDriver && syncStatus && (
          <span
            data-testid="sync-badge"
            className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 font-semibold ${syncStatus === "synced" ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${syncStatus === "synced" ? "bg-emerald-400" : "bg-amber-400 animate-pulse"}`} />
            {syncStatus === "synced" ? "In sync" : "Catching up"}
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          {/* Co-driver management (drivers only) */}
          {isDriver && (
            <div className="relative">
              {seatsFull ? (
                <button
                  data-testid="stepdown-button"
                  onClick={onStepDown}
                  title="Step down to passenger"
                  className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1 font-semibold text-[#A0A0AB] hover:text-white hover:bg-white/10 transition-colors"
                >
                  <LogOut className="w-3 h-3" /> Step down
                </button>
              ) : (
                <button
                  data-testid="invite-codriver-button"
                  onClick={() => setInviteOpen((v) => !v)}
                  title="Invite a co-driver"
                  className="flex items-center gap-1.5 rounded-full border border-[#00D9FF]/40 bg-[#00D9FF]/15 px-3 py-1 font-semibold text-[#F1F1F5] hover:bg-[#00D9FF]/25 transition-colors"
                >
                  <UserPlus className="w-3 h-3 text-[#00D9FF]" /> Invite Co-Driver
                </button>
              )}
              {inviteOpen && !seatsFull && (
                <>
                  <div className="fixed inset-0 z-[70]" onClick={() => setInviteOpen(false)} />
                  <div data-testid="invite-codriver-menu" className="absolute right-0 mt-2 w-56 z-[71] bg-[#0F1636] border border-white/10 rounded-2xl p-2 shadow-2xl max-h-64 overflow-y-auto chat-scroll">
                    <p className="text-[10px] font-bold text-[#71717A] uppercase tracking-wider px-2 py-1">Pick a co-driver</p>
                    {candidates.length === 0 ? (
                      <p className="text-xs text-[#71717A] px-2 py-2">No one else to invite yet.</p>
                    ) : candidates.map((v) => (
                      <button
                        key={v.id}
                        data-testid={`invite-option-${v.name}`}
                        onClick={() => { onInviteCoDriver && onInviteCoDriver(v.id); setInviteOpen(false); }}
                        className="w-full flex items-center gap-2 px-2 py-2 rounded-xl hover:bg-white/5 transition-colors text-left"
                      >
                        <span className="text-lg">{v.flag}</span>
                        <span className="text-sm text-[#F1F1F5] font-medium truncate">{v.name}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          <button
            data-testid="upnext-toggle"
            onClick={() => setQueueOpen((v) => !v)}
            className={`btn-neon flex items-center gap-1.5 rounded-full border px-3 py-1 font-semibold transition-all ${queueOpen ? "bg-[#9D4EDD]/20 border-[#9D4EDD] text-[#9D4EDD] glow-purple" : "bg-white/5 border-[#9D4EDD]/25 text-[#9D4EDD]/80 hover:border-[#9D4EDD] hover:text-[#9D4EDD]"}`}
          >
            <ListVideo className="w-3 h-3" /> Up Next{upNext.length ? ` (${upNext.length})` : ""}
          </button>
          {isDriver && browseUrl && (
            <button
              data-testid="resync-button"
              onClick={onResync}
              title="Resend the current URL to everyone (no reload for those already watching)"
              className="btn-neon flex items-center gap-1.5 rounded-full bg-[#FF006E]/12 border border-[#FF006E]/50 px-3 py-1 font-semibold text-[#FF006E] hover:bg-[#FF006E]/25 hover:border-[#FF006E] transition-all"
            >
              <RefreshCw className="w-3 h-3" /> Resync
            </button>
          )}
        </div>
      </div>

      {/* Up Next queue */}
      {queueOpen && (
        <div data-testid="upnext-panel" className="border-b border-white/5 bg-[#0A0E27] shrink-0 px-4 py-3 max-h-56 overflow-y-auto chat-scroll">
          {isDriver && (
            <div className="flex items-center gap-2 mb-2">
              <input
                data-testid="upnext-input"
                value={nextUrl}
                onChange={(e) => setNextUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && nextUrl.trim()) { onAddNext(nextUrl.trim()); setNextUrl(""); } }}
                placeholder="Paste a YouTube link to queue…"
                className="flex-1 h-9 bg-[#0F1636] border border-white/10 rounded-full px-4 text-sm text-[#F1F1F5] outline-none focus:border-[#00D9FF] placeholder:text-[#71717A]"
              />
              <button
                data-testid="upnext-add-button"
                onClick={() => { if (nextUrl.trim()) { onAddNext(nextUrl.trim()); setNextUrl(""); } }}
                className="h-9 px-3 bg-[#00D9FF] text-navy-ink rounded-full font-bold text-sm flex items-center gap-1 hover:bg-[#33E4FF] transition-colors active:scale-95"
              >
                <Plus className="w-3.5 h-3.5" /> Add
              </button>
            </div>
          )}
          {upNext.length === 0 ? (
            <p className="text-[#71717A] text-xs py-3 text-center">Nothing queued yet. {isDriver ? "Line up the next video above." : "The driver hasn't queued anything."}</p>
          ) : (
            <div className="space-y-1.5">
              {upNext.map((it, idx) => {
                const vid = extractVideoId(it.url);
                return (
                  <div key={it.id} data-testid="upnext-item" className="flex items-center gap-2.5 bg-white/5 rounded-xl p-1.5 pr-2">
                    <span className="text-[10px] font-bold text-[#71717A] w-4 text-center shrink-0">{idx + 1}</span>
                    {vid ? (
                      <img src={`https://img.youtube.com/vi/${vid}/mqdefault.jpg`} alt="" className="w-16 h-9 rounded object-cover shrink-0" />
                    ) : (
                      <div className="w-16 h-9 rounded bg-[#0F1636] flex items-center justify-center shrink-0"><Globe className="w-4 h-4 text-[#71717A]" /></div>
                    )}
                    <span className="flex-1 text-xs text-[#E4E4E7] truncate">{getHost(it.url) || it.url}</span>
                    {isDriver && (
                      <>
                        <button data-testid="upnext-play-button" onClick={() => onPlayNext(it.id)} title="Play now" className="w-7 h-7 rounded-full bg-white/5 hover:bg-[#00D9FF]/30 text-white flex items-center justify-center transition-colors">
                          <Play className="w-3.5 h-3.5" />
                        </button>
                        <button data-testid="upnext-remove-button" onClick={() => onRemoveNext(it.id)} title="Remove" className="w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 text-[#A0A0AB] flex items-center justify-center transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* stage */}
      <div className="relative flex-1 min-h-0 bg-[#0A0E27]">
        {!isDriver && pickerActive && (
          <div data-testid="driver-picking-banner" className="absolute inset-x-0 top-0 z-30 flex justify-center pointer-events-none">
            <span className="mt-3 bg-black/80 text-white text-sm px-4 py-2 rounded-full backdrop-blur flex items-center gap-2">
              <Search className="w-3.5 h-3.5 text-[#00D9FF]" /> {pickerActive} is picking a video…
            </span>
          </div>
        )}
        {!browseUrl ? (
          !canControl ? (
            <div data-testid="driver-selecting-msg" className="w-full h-full flex flex-col items-center justify-center gap-3 px-6 text-center">
              <div className="w-11 h-11 rounded-full border-2 border-white/15 border-t-[#00D9FF] animate-spin" />
              <p className="text-[#F1F1F5] font-semibold">
                {lead ? `${lead.flag} ${lead.name}` : "The driver"} is choosing a video…
              </p>
              <p className="text-[#71717A] text-sm max-w-xs">Sit tight — the next video starts for everyone automatically.</p>
            </div>
          ) : (
          <div className="w-full h-full overflow-y-auto chat-scroll flex flex-col items-center justify-center py-8">
            {canControl && (
              <button
                data-testid="home-search-button"
                onClick={openSearch}
                className="btn-neon glow-cyan flex items-center gap-2 h-12 px-6 bg-[#00D9FF] text-navy-ink font-display font-bold uppercase tracking-wider rounded-full hover:bg-[#33E4FF] active:scale-95 mb-8"
              >
                <Search className="w-5 h-5" /> Search for videos to watch
              </button>
            )}
            <StreamingServices onStart={onNavigate} canStart={canControl} />
            <p className="text-[#71717A] text-xs mt-6 text-center px-6">
              …or paste any website URL in the bar above to browse together.
            </p>
          </div>
          )
        ) : (
          <>
            {canControl && (
              <button
                data-testid="change-video-button"
                onClick={onBack}
                title="Back to video selection"
                className="absolute top-3 max-md:top-14 left-3 z-30 flex items-center gap-1.5 h-9 pl-2 pr-3.5 rounded-full bg-black/60 backdrop-blur-md border border-white/10 text-white text-sm font-semibold hover:bg-black/80 hover:border-[#00D9FF]/50 active:scale-95 transition-all"
              >
                <ArrowLeft className="w-4 h-4" /> Change video
              </button>
            )}
            <VideoPlayer url={browseUrl} playerRef={playerRef} onAction={onAction} onStatus={onVideoStatus} />
            <NowPlayingBanner url={browseUrl} />
            {extractVideoId(browseUrl) && <BufferingSpinner playerRef={playerRef} />}
            {extractVideoId(browseUrl) && (
              <SubtitleOverlay
                videoId={extractVideoId(browseUrl)}
                playerRef={playerRef}
                viewerLanguage={viewerLanguage}
                isDriver={isDriver}
                onUploadCaptions={onUploadCaptions}
                onSeek={onSeek}
                refreshSignal={captionSignal}
              />
            )}
            {!isDriver && !isBlockedSite(browseUrl) && !extractVideoId(browseUrl) && (
              <div className="absolute inset-0 z-10 cursor-not-allowed" title="The driver is browsing — you're watching">
                <span className="absolute top-3 left-1/2 -translate-x-1/2 bg-black/70 text-white text-xs px-3 py-1.5 rounded-full backdrop-blur">
                  👀 Watching {lead ? lead.name : "the driver"}
                </span>
              </div>
            )}
            {isDriver && !isBlockedSite(browseUrl) && !extractVideoId(browseUrl) && (
              <ExternalSyncNotice browseUrl={browseUrl} />
            )}
          </>
        )}
      </div>

      <SearchPanel open={searchOpen} onClose={closeSearch} onPick={pickVideo} region={region} />
    </div>
  );
}
